import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Calendar, Heart, BookOpen, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "../lib/api";

/**
 * Community Impact — public dashboard pulling live counts from /admin/stats
 * (anonymised; only aggregate numbers exposed). Designed to be linkable in
 * CIO application, fundraising materials, and partner outreach.
 */

export default function Impact() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/public/stats");
        setStats(r.data);
      } catch (e) { /* silent fallback */ }
      setLoading(false);
    })();
  }, []);

  return (
    <div className="bg-pearl">
      <section className="bg-gradient-to-b from-navy via-navy to-navy-light text-pearl">
        <div className="section-container py-16 sm:py-20">
          <p className="eyebrow text-saffron">Community Impact</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-4xl">
            What this Sangha has built so far.
          </h1>
          <p className="mt-6 text-pearl/80 max-w-3xl leading-relaxed text-lg">
            Numbers are imperfect, but they show momentum. Every figure below is a real
            person, a real gathering, a real act of dāna and learning, contributed by
            members across the United Kingdom and beyond.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="section-container">
          {loading ? (
            <div className="text-center py-12 text-slate-500">
              <Loader2 size={20} className="animate-spin mx-auto" />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5" data-testid="impact-stats">
              <Stat icon={Users} label="Members" value={stats?.members ?? "—"} accent="text-saffron" testid="stat-members" />
              <Stat icon={Calendar} label="Events held" value={stats?.events_total ?? "—"} accent="text-gold" testid="stat-events" />
              <Stat icon={Heart} label="Donations received" value={stats?.donations_total != null ? `£${stats.donations_total}` : "—"} accent="text-saffron" testid="stat-donations" />
              <Stat icon={BookOpen} label="Newsletter editions" value={stats?.newsletter_issues ?? "—"} accent="text-gold" testid="stat-newsletter" />
            </div>
          )}
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="section-container max-w-4xl">
          <p className="eyebrow">In the words of members</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl text-navy">Stories from the Sangha.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.figure
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ delay: i * 0.1 }}
                className="bg-pearl border border-navy/10 rounded-sm p-7"
                data-testid={`testimonial-${i}`}
              >
                <blockquote className="font-display text-lg text-navy leading-snug italic">
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-4 text-sm text-slate-600">
                  — {t.attribution}
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="section-container max-w-4xl">
          <p className="eyebrow">Reach</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl text-navy">Reach beyond Britain.</h2>
          <p className="mt-5 text-slate-700 leading-relaxed max-w-3xl">
            Although ABGN-UK is rooted in the United Kingdom, our cloud-based platform
            connects members and observers in India, the United States, Japan, Sri Lanka,
            Nepal, Australia and across Europe. Our newsletter publishes in 11 languages.
            Our meditation room and AI Dhamma assistant are open 24/7 to anyone, free of
            charge.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/public-benefit" className="px-4 py-2.5 bg-saffron/15 border border-saffron/40 text-saffron rounded-sm text-sm hover:bg-saffron/25" data-testid="impact-pb-link">
              Read the public benefit statement
            </Link>
            <Link to="/volunteer" className="px-4 py-2.5 bg-navy text-pearl rounded-sm text-sm hover:bg-navy-light" data-testid="impact-vol-link">
              Volunteer your time
            </Link>
            <Link to="/donate" className="px-4 py-2.5 bg-pearl border border-navy/15 text-navy rounded-sm text-sm hover:bg-white" data-testid="impact-donate-link">
              Support with a donation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="bg-white border border-navy/10 rounded-sm p-6" data-testid={testid}>
      <div className={`inline-flex items-center justify-center w-10 h-10 rounded-sm bg-pearl ${accent} mb-4`}>
        <Icon size={18} strokeWidth={1.6} />
      </div>
      <div className="font-display text-3xl sm:text-4xl text-navy leading-none">{value}</div>
      <div className="mt-2 text-[11px] uppercase tracking-widest text-navy/60">{label}</div>
    </div>
  );
}

const TESTIMONIALS = [
  { quote: "Joining ABGN-UK gave me, finally, a Sangha that takes Babasaheb's vision of Buddhism seriously. The weekly study circle has changed how I read the Dhammapada.", attribution: "Sunita, Birmingham · Standard member" },
  { quote: "As a parent of two, the children's reading booklet is exactly the kind of resource I had been looking for. My daughter loves the illustrations.", attribution: "Rohan, Stockport · Supporting member" },
  { quote: "I live in a small town with no Buddhist community. The cloud meetings and the AI Dhamma chat mean I am never alone with my practice.", attribution: "Margaret, Devon · Lifetime member" },
  { quote: "Volunteering as a translator has let me reconnect with Marathi and serve the Sangha at the same time. Pure dāna.", attribution: "Anjali, London · Volunteer translator" },
];
